export interface StepEducationalContent {
  overview: string;
  whatIsInvolved: string[];
  whyImportant: string[];
  preparesYouFor: string[];
  keyOutcomes: string[];
  estimatedTimeBreakdown: { task: string; time: string }[];
  tips: string[];
  commonMistakes: string[];
  resources: { title: string; description: string; link?: string }[];
}

export const journeyEducationalContent: Record<string, StepEducationalContent> = {
  'get-started': {
    overview: "Set up your foundation for a successful homebuying journey by creating your profile and defining your preferences.",
    whatIsInvolved: [
      "Create your personal profile with basic information",
      "Define your homebuying goals and timeline",
      "Set location preferences and must-have features",
      "Establish your budget range and financing preferences",
      "Connect your financial accounts (optional)"
    ],
    whyImportant: [
      "Creates a personalized experience tailored to your needs",
      "Helps the system provide accurate recommendations",
      "Saves time by pre-filling information throughout the platform",
      "Enables progress tracking and milestone celebrations",
      "Builds your comprehensive homebuyer profile"
    ],
    preparesYouFor: [
      "Financial assessment with personalized recommendations",
      "Property searches that match your criteria",
      "Market analysis based on your preferred locations",
      "Accurate buying power calculations",
      "Streamlined application processes with lenders"
    ],
    keyOutcomes: [
      "Complete homebuyer profile",
      "Clear goal definition",
      "Personalized platform experience",
      "Foundation for accurate financial analysis"
    ],
    estimatedTimeBreakdown: [
      { task: "Basic profile setup", time: "2 minutes" },
      { task: "Goal setting and preferences", time: "3 minutes" }
    ],
    tips: [
      "Be honest about your budget - it helps get better recommendations",
      "Include both must-haves and nice-to-haves in your preferences",
      "Consider future needs, not just current ones",
      "Save multiple location options to expand your search"
    ],
    commonMistakes: [
      "Setting an unrealistic budget range",
      "Being too narrow with location preferences",
      "Not considering future family or lifestyle changes",
      "Skipping optional fields that improve recommendations"
    ],
    resources: [
      {
        title: "First-Time Homebuyer Guide",
        description: "Complete guide covering the entire homebuying process"
      },
      {
        title: "Setting Realistic Expectations",
        description: "How to set achievable goals for your home search"
      }
    ]
  },
  'financial-assessment': {
    overview: "Calculate your true buying power and understand exactly how much home you can afford with confidence.",
    whatIsInvolved: [
      "Income verification and analysis",
      "Credit score review and improvement strategies",
      "Debt-to-income ratio calculation",
      "Down payment and closing cost planning",
      "Pre-qualification scenarios and simulations",
      "Mortgage rate and term comparisons"
    ],
    whyImportant: [
      "Prevents you from looking at homes outside your budget",
      "Strengthens your position when making offers",
      "Identifies areas for financial improvement",
      "Provides realistic timeline for your purchase",
      "Helps avoid costly financial mistakes"
    ],
    preparesYouFor: [
      "Confident home shopping within your budget",
      "Strong mortgage pre-approval applications",
      "Effective negotiation with sellers",
      "Strategic timing of your home purchase",
      "Backup plan development"
    ],
    keyOutcomes: [
      "Accurate buying power calculation",
      "Pre-qualification roadmap",
      "Credit improvement plan",
      "Down payment savings strategy",
      "Mortgage comparison analysis"
    ],
    estimatedTimeBreakdown: [
      { task: "Income and employment information", time: "5 minutes" },
      { task: "Debt and credit analysis", time: "5 minutes" },
      { task: "Down payment and savings review", time: "3 minutes" },
      { task: "Scenario analysis and planning", time: "2 minutes" }
    ],
    tips: [
      "Gather recent pay stubs and tax returns beforehand",
      "Check your credit report for errors before starting",
      "Consider all sources of income, including bonuses",
      "Factor in potential rate changes when planning"
    ],
    commonMistakes: [
      "Overestimating how much house you can afford",
      "Forgetting about closing costs and moving expenses",
      "Not accounting for property taxes and insurance",
      "Ignoring the impact of debt on buying power"
    ],
    resources: [
      {
        title: "Mortgage Calculator",
        description: "Interactive tool to calculate monthly payments"
      },
      {
        title: "Credit Score Improvement Guide",
        description: "Step-by-step strategies to boost your credit"
      },
      {
        title: "Down Payment Assistance Programs",
        description: "Find local and national assistance programs"
      }
    ]
  },
  'market-analysis': {
    overview: "Research and analyze properties in your target markets to make informed decisions about where and what to buy.",
    whatIsInvolved: [
      "Property search with advanced filters",
      "Market trend analysis and price comparisons",
      "Neighborhood research and scoring",
      "School district and amenity analysis",
      "Future development and growth projections",
      "Investment potential evaluation"
    ],
    whyImportant: [
      "Ensures you buy in the right location for your needs",
      "Helps identify the best value opportunities",
      "Prevents overpaying in competitive markets",
      "Reveals potential red flags early",
      "Maximizes your long-term investment return"
    ],
    preparesYouFor: [
      "Confident property selection and comparison",
      "Strategic offer timing and pricing",
      "Effective negotiation with market data",
      "Long-term satisfaction with your choice",
      "Potential resale value optimization"
    ],
    keyOutcomes: [
      "Comprehensive market knowledge",
      "Property shortlist with detailed analysis",
      "Neighborhood comparison reports",
      "Price trend insights",
      "Investment potential rankings"
    ],
    estimatedTimeBreakdown: [
      { task: "Property search and filtering", time: "8 minutes" },
      { task: "Market trend analysis", time: "5 minutes" },
      { task: "Neighborhood research", time: "4 minutes" },
      { task: "Comparative analysis", time: "3 minutes" }
    ],
    tips: [
      "Look at recently sold properties, not just active listings",
      "Visit neighborhoods at different times of day",
      "Research planned developments that could affect values",
      "Consider your daily commute and lifestyle needs"
    ],
    commonMistakes: [
      "Focusing only on the house, not the neighborhood",
      "Not researching future development plans",
      "Ignoring school districts even without children",
      "Overlooking resale potential"
    ],
    resources: [
      {
        title: "Neighborhood Analysis Tool",
        description: "Compare neighborhoods on multiple factors"
      },
      {
        title: "Market Trend Reports",
        description: "Latest market data and forecasts"
      },
      {
        title: "School District Ratings",
        description: "Educational quality and performance data"
      }
    ]
  },
  'compare-options': {
    overview: "Systematically compare your top property choices to identify the best option for your needs and budget.",
    whatIsInvolved: [
      "Side-by-side property comparisons",
      "Cost-benefit analysis for each option",
      "Pros and cons evaluation matrix",
      "Total cost of ownership calculations",
      "Future appreciation potential assessment",
      "Decision scoring and ranking system"
    ],
    whyImportant: [
      "Removes emotion from decision-making process",
      "Ensures you consider all important factors",
      "Helps identify the best long-term value",
      "Prevents buyer's remorse after purchase",
      "Strengthens confidence in your final choice"
    ],
    preparesYouFor: [
      "Final property selection decision",
      "Offer strategy development",
      "Inspection and appraisal priorities",
      "Negotiation talking points",
      "Backup option planning"
    ],
    keyOutcomes: [
      "Detailed property comparison matrix",
      "Objective scoring and ranking",
      "Clear winner identification",
      "Backup options prioritization",
      "Decision confidence report"
    ],
    estimatedTimeBreakdown: [
      { task: "Property comparison setup", time: "3 minutes" },
      { task: "Detailed analysis and scoring", time: "5 minutes" },
      { task: "Review and final ranking", time: "2 minutes" }
    ],
    tips: [
      "Include both financial and lifestyle factors",
      "Weight factors based on your personal priorities",
      "Consider renovation potential and costs",
      "Think about your 5-10 year plans"
    ],
    commonMistakes: [
      "Comparing too many properties at once",
      "Focusing only on price or only on features",
      "Not weighting factors by importance",
      "Ignoring hidden costs and maintenance needs"
    ],
    resources: [
      {
        title: "Property Comparison Tool",
        description: "Interactive comparison matrix builder"
      },
      {
        title: "Hidden Costs Calculator",
        description: "Estimate maintenance and ownership costs"
      },
      {
        title: "Renovation Cost Estimator",
        description: "Budget for potential improvements"
      }
    ]
  },
  'final-decision': {
    overview: "Review all analysis and reports to make your final decision and prepare for the offer and closing process.",
    whatIsInvolved: [
      "Comprehensive report review and analysis",
      "Final financial verification and approval",
      "Offer strategy development and timing",
      "Inspection and appraisal planning",
      "Closing timeline and milestone setup",
      "Next steps and action plan creation"
    ],
    whyImportant: [
      "Ensures all factors have been thoroughly considered",
      "Minimizes risks and surprises during closing",
      "Provides a clear roadmap for next steps",
      "Builds confidence for offer negotiations",
      "Sets realistic expectations for the process"
    ],
    preparesYouFor: [
      "Making competitive and strategic offers",
      "Smooth inspection and appraisal processes",
      "Effective closing coordination",
      "Moving and transition planning",
      "Long-term homeownership success"
    ],
    keyOutcomes: [
      "Complete decision documentation",
      "Offer strategy and pricing guidance",
      "Closing timeline and checklist",
      "Risk assessment and mitigation plan",
      "Next steps action plan"
    ],
    estimatedTimeBreakdown: [
      { task: "Final report review", time: "5 minutes" },
      { task: "Decision confirmation", time: "3 minutes" },
      { task: "Offer strategy planning", time: "4 minutes" },
      { task: "Next steps setup", time: "3 minutes" }
    ],
    tips: [
      "Don't rush the final decision - take time to review",
      "Have your financing pre-approval ready",
      "Plan your offer strategy before you need it",
      "Prepare for multiple scenarios (acceptance, rejection, counter)"
    ],
    commonMistakes: [
      "Making decisions based on emotions alone",
      "Not having backup options ready",
      "Waiting too long and losing the property",
      "Not understanding the local offer process"
    ],
    resources: [
      {
        title: "Offer Strategy Guide",
        description: "How to make competitive offers in any market"
      },
      {
        title: "Closing Process Checklist",
        description: "Step-by-step guide through closing"
      },
      {
        title: "First-Year Homeowner Tips",
        description: "Essential guidance for new homeowners"
      }
    ]
  }
};